import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API } from '../App';
import { NavBar } from '../components/NavBar';
import { useSound } from '../hooks/useSound';
import { CurriculumMapButton } from '../components/CurriculumMap';
import { Play, AlertTriangle, Info, Eye, EyeOff, ChevronDown, ChevronUp, Monitor, ListOrdered, Check, Plus, X, GraduationCap } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Robot Balayeur content type labels (for passage picker)
const CONTENT_TYPE_INFO = {
  vocabulary_theme: { label: 'Vocabulary', color: '#4C6EF5' },
  sentence_reading: { label: 'Sentences', color: '#0CA678' },
  phonics_pattern: { label: 'Phonics', color: '#7950F2' },
  minimal_pairs: { label: 'Minimal Pairs', color: '#F06595' },
  fluency_passage: { label: 'Fluency', color: '#E03131' },
  tongue_twister: { label: 'Tongue Twister', color: '#F59F00' },
  number_sequence: { label: 'Numbers', color: '#1971C2' },
  verb_conjugation: { label: 'Conjugation', color: '#E03131' },
};


const MONTH_FR = {
  september: 'Septembre', october: 'Octobre', november: 'Novembre',
  december: 'Decembre', january: 'Janvier', february: 'Fevrier',
  march: 'Mars', april: 'Avril', may: 'Mai', june: 'Juin',
};
const LEVEL_LABELS = { 'pre-A1': 'Beginner', 'A1': 'Elementary', 'A1-A2': 'Intermediate', 'A2': 'Upper Intermediate' };
const TOPIC_LABELS = {
  greetings_introductions: 'Greetings & Introductions', numbers_time: 'Numbers & Time',
  classroom_school: 'Classroom & School', family_friends: 'Family & Friends',
  food_cooking: 'Food & Cooking', clothing_colours: 'Clothing & Colours',
  weather_seasons: 'Weather & Seasons', animals_nature: 'Animals & Nature',
  body_health: 'Body & Health', community_places: 'Community & Places',
  sports_hobbies: 'Sports & Hobbies', daily_routines: 'Daily Routines',
  holidays_celebrations: 'Holidays & Celebrations', canadian_culture: 'Canadian Culture',
  grammar_verbs: 'Grammar & Verbs', past_tense_advanced: 'Past Tense & Advanced',
  pronunciation_phonics: 'Pronunciation & Sounds',
};

// Per-game configuration: what settings make sense for each game
const GAME_CONFIG = {
  la_course: {
    name: 'La Course', desc: 'Two teams race canoes by answering questions. First to reach the target wins!',
    itemLabel: 'questions',
    participation: ['teams', 'head_to_head'],
    showGameTimer: true, // game-level countdown (minutes)
    showTarget: true,    // "first to X" target
    showPerQuestionTimer: false,
    showRounds: false,
    showGrid: false,
    defaultParticipation: 'teams',
  },
  duel_conjugaison: {
    name: 'Le Duel', desc: 'Two teams face off on the ice rink. Answer conjugation questions to score goals!',
    itemLabel: 'questions',
    participation: ['teams', 'head_to_head'],
    showGameTimer: true,
    showTarget: true,
    showPerQuestionTimer: false,
    showRounds: false,
    showGrid: false,
    defaultParticipation: 'teams',
  },
  la_tour: {
    name: 'La Tour', desc: 'Stack blocks by answering correctly. Wrong answers cost lives. Reach the target height!',
    itemLabel: 'questions',
    participation: ['solo', 'whole_class'],
    showGameTimer: false,
    showTarget: true, // target height
    showPerQuestionTimer: true, // per-question timer makes sense here
    showRounds: false,
    showGrid: false,
    defaultParticipation: 'whole_class',
  },
  memoire: {
    name: 'La Mémoire', desc: 'Flip cards to find matching pairs. Match French-English, verb forms, and more!',
    itemLabel: 'sets',
    participation: ['whole_class'],
    showGameTimer: false,
    showTarget: false,
    showPerQuestionTimer: false,
    showRounds: true,
    showGrid: false,
    defaultParticipation: 'whole_class',
  },
  avalanche: {
    name: "L'Avalanche", desc: 'Words fall from the sky! Tap the correct ones before they hit the ground.',
    itemLabel: 'word sets',
    participation: ['solo', 'whole_class'],
    showGameTimer: false,
    showTarget: false,
    showPerQuestionTimer: false,
    showRounds: true, // how many sets
    showGrid: false,
    defaultParticipation: 'whole_class',
  },
  tri_express: {
    name: 'Tri Express', desc: 'Sort words into the correct categories as fast as you can!',
    itemLabel: 'sorting sets',
    participation: ['whole_class'],
    showGameTimer: false,
    showTarget: false,
    showPerQuestionTimer: false,
    showRounds: true,
    showGrid: false,
    defaultParticipation: 'whole_class',
  },
  batisseur: {
    name: 'Batisseur', desc: 'Tap words in the correct order to build French sentences.',
    itemLabel: 'sentences',
    participation: ['whole_class'],
    showGameTimer: false,
    showTarget: false,
    showPerQuestionTimer: false,
    showRounds: true,
    showGrid: false,
    defaultParticipation: 'whole_class',
  },
  robot_balayeur: {
    name: 'Robot Balayeur', desc: 'Guided reading practice. The robot highlights each word while the class reads aloud together.',
    itemLabel: 'reading passages',
    participation: ['whole_class'],
    showGameTimer: false,
    showTarget: false,
    showPerQuestionTimer: false,
    showRounds: true, // how many passages to read
    showGrid: false,
    defaultParticipation: 'whole_class',
  },
  horloge_folle: {
    name: "L'Horloge Folle", desc: 'Type the French word for numbers and times. Build a streak to make the clock go wild!',
    itemLabel: 'questions',
    participation: ['solo', 'whole_class'],
    showGameTimer: false,
    showTarget: false,
    showPerQuestionTimer: false,
    showRounds: true,
    showGrid: false,
    defaultParticipation: 'whole_class',
  },
  detective: {
    name: 'Le Detective', desc: 'Listen to French audio clues and answer comprehension questions.',
    itemLabel: 'cases',
    participation: ['whole_class'],
    showGameTimer: false,
    showTarget: false,
    showPerQuestionTimer: false,
    showRounds: true,
    showGrid: false,
    defaultParticipation: 'whole_class',
  },
  carte_du_canada: {
    name: 'Carte du Canada', desc: 'Answer geography and culture questions to discover locations on the map of Canada!',
    itemLabel: 'questions',
    participation: ['whole_class'],
    showGameTimer: false,
    showTarget: false,
    showPerQuestionTimer: false,
    showRounds: true,
    showGrid: false,
    defaultParticipation: 'whole_class',
  },
  le_chef: {
    name: 'Le Chef', desc: 'Follow recipe steps in French to cook Canadian dishes!',
    itemLabel: 'recipes',
    participation: ['whole_class'],
    showGameTimer: false,
    showTarget: false,
    showPerQuestionTimer: false,
    showRounds: false,
    showGrid: false,
    defaultParticipation: 'whole_class',
  },
};

// Minimum playable item thresholds per game type
// Rich set games (passages, memory sets) need fewer items because each item is a full activity
// Quick MCQ games need more items for a meaningful session
// Team games use TWO thresholds: trigger (16) = when to start merging, stop (25) = when to stop expanding
const MIN_ITEMS_THRESHOLD = {
  la_course: 16,
  duel_conjugaison: 16,
  la_tour: 5,
  horloge_folle: 5,
  carte_du_canada: 5,
  detective: 4,
  batisseur: 3,
  avalanche: 2,
  tri_express: 2,
  memoire: 2,
  robot_balayeur: 2,
  le_chef: 1,
};

// Team games: after merge is triggered, keep expanding until combined total ≥ this
// Ensures enough questions for both teams (25 total = ~12 per team)
const TEAM_STOP_THRESHOLD = 25;

const DIFFICULTY_ORDER = ['introductory', 'practice', 'mastery'];

// Compute merged difficulties when a selected difficulty is too thin
// triggerThreshold: individual count below this starts merging
// stopThreshold: keep expanding until combined total reaches this (defaults to triggerThreshold)
// For team games: trigger=16 (per-difficulty), stop=25 (combined total for 12+ per team)
function computeMergedDifficulties(selectedDiff, contentCounts, triggerThreshold, stopThreshold) {
  const selectedCount = contentCounts[selectedDiff] || 0;
  if (selectedCount >= triggerThreshold) {
    return { difficulties: [selectedDiff], totalCount: selectedCount, merged: false, addedDiffs: [] };
  }

  const effectiveStop = stopThreshold || triggerThreshold;
  const selectedIdx = DIFFICULTY_ORDER.indexOf(selectedDiff);
  const included = [selectedDiff];
  let total = selectedCount;
  const addedDiffs = [];

  // Try adding higher (harder) difficulties first
  for (let i = selectedIdx + 1; i < DIFFICULTY_ORDER.length && total < effectiveStop; i++) {
    const d = DIFFICULTY_ORDER[i];
    const c = contentCounts[d] || 0;
    if (c > 0) { included.push(d); total += c; addedDiffs.push(d); }
  }

  // If still not enough, try lower (easier) difficulties
  for (let i = selectedIdx - 1; i >= 0 && total < effectiveStop; i--) {
    const d = DIFFICULTY_ORDER[i];
    const c = contentCounts[d] || 0;
    if (c > 0) { included.unshift(d); total += c; addedDiffs.push(d); }
  }

  // Sort in pedagogical order
  included.sort((a, b) => DIFFICULTY_ORDER.indexOf(a) - DIFFICULTY_ORDER.indexOf(b));

  return { difficulties: included, totalCount: total, merged: included.length > 1, addedDiffs };
}

// Map horloge_folle modes to type keys
function getHorlogeTypeKey(mode) {
  if (mode === 'see_produce_french') return 'numbers';
  if (mode === 'math_in_french') return 'french_math';
  if (['hear_type_number', 'hear_type_time', 'hear_produce_time'].includes(mode)) return 'listening';
  return 'time'; // see_clock_produce_french, time_reading, read_set_clock, time_with_future
}

// Map la_tour exercise types to teacher-visible categories
function getLatourTypeKey(exerciseType) {
  if (['article_selection', 'preposition_selection'].includes(exerciseType)) return 'articles';
  if (['conjugation_drill', 'verb_selection', 'auxiliary_selection', 'agreement_selection'].includes(exerciseType)) return 'conjugation';
  if (['tense_sorting', 'sentence_transformation'].includes(exerciseType)) return 'tenses';
  return 'vocabulary'; // word_image_speed_match, comparison_selection
}

// Map tri_express sort_types to teacher-visible categories
function getTriTypeKey(sortType) {
  if (sortType === 'vocabulary_category') return 'vocabulary';
  if (sortType === 'gender') return 'gender';
  if (['tense', 'tense_sorting'].includes(sortType)) return 'tenses';
  return 'grammar'; // comparison_type, conjunction_type, verb_type, auxiliary_selection, grammar_category, etc.
}

// Top-right Teach First pill: an info "i" button that explains the two ways
// to run a game, paired with a "Teach First" CTA. Browse Games + camp
// game-direct only; suppressed for slide-taught lesson/program flows.
function TeachFirstTopRight({ onClick, disabled }) {
  const [showInfo, setShowInfo] = useState(false);
  return (
    <div data-testid="teach-first-top-right" style={{
      position: 'absolute', top: '1rem', right: '1.25rem',
      display: 'flex', alignItems: 'center', gap: '0.6rem', zIndex: 5,
    }}>
      <span data-testid="teach-first-or" style={{
        fontFamily: 'Nunito, sans-serif', fontSize: '0.95rem', fontStyle: 'italic',
        color: 'var(--text-secondary)', opacity: 0.75, userSelect: 'none',
      }}>
        or
      </span>
      <div style={{ position: 'relative' }}>
        <button
          data-testid="teach-first-info"
          type="button"
          onClick={() => setShowInfo(v => !v)}
          onBlur={() => setTimeout(() => setShowInfo(false), 120)}
          aria-label="How Teach First works"
          style={{
            width: 28, height: 28, borderRadius: '50%',
            border: '2px solid var(--border)', background: 'var(--surface)',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-secondary)', padding: 0,
          }}
        >
          <Info size={15} />
        </button>
        {showInfo && (
          <div data-testid="teach-first-info-popover" style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 320,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: '0.75rem', padding: '0.85rem 1rem',
            boxShadow: '0 12px 30px rgba(30,33,64,0.12)',
            fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem',
            color: 'var(--text-primary)', lineHeight: 1.45, textAlign: 'left',
          }}>
            <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>Two ways to run this game</div>
            <div style={{ marginBottom: '0.35rem' }}>
              <strong>1.</strong> Hit <em>Start Game</em> at the bottom and have students play straight away. Review answers after the round.
            </div>
            <div>
              <strong>2.</strong> Tap <em>Teach First</em> to walk the class through the items that will appear in the game, then start the round when you are ready.
            </div>
          </div>
        )}
      </div>
      <button
        data-testid="teach-first-button"
        type="button"
        onClick={onClick}
        disabled={disabled}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
          padding: '0.55rem 1rem', borderRadius: '999px',
          border: '2px solid var(--primary, #4C6EF5)',
          background: disabled ? 'var(--surface)' : 'var(--primary, #4C6EF5)',
          color: disabled ? 'var(--text-secondary)' : 'white',
          fontFamily: 'Fredoka, sans-serif', fontSize: '0.95rem', fontWeight: 700,
          cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
          boxShadow: disabled ? 'none' : '0 4px 14px rgba(76,110,245,0.25)',
          transition: 'all 0.12s',
        }}
      >
        <GraduationCap size={18} /> Teach First
      </button>
    </div>
  );
}

function PreviewSection({ previewOpen, setPreviewOpen, previewLoading, previewItems, gameName }) {
  const isMemoireGame = gameName === 'memoire';
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <button data-testid="preview-toggle" onClick={() => setPreviewOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.4rem 0.85rem', borderRadius: '0.6rem',
          background: previewOpen ? 'rgba(76,110,245,0.08)' : 'transparent',
          border: '1.5px solid rgba(76,110,245,0.25)', color: 'var(--primary)',
          fontFamily: 'Fredoka', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
        }}>
        {previewOpen ? <EyeOff size={15} /> : <Eye size={15} />}
        {previewOpen ? 'Hide' : 'Preview'} {isMemoireGame ? 'Sets' : 'Questions'}
        {previewOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {previewOpen && (
        <div data-testid="preview-list" style={{
          marginTop: '0.5rem', maxHeight: 320, overflowY: 'auto',
          background: 'var(--surface)', borderRadius: '0.75rem',
          border: '1.5px solid var(--border)', padding: '0.5rem',
        }}>
          {previewLoading ? (
            <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontFamily: 'Fredoka' }}>Loading...</div>
          ) : previewItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontFamily: 'Fredoka' }}>No items found</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Nunito', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '0.35rem 0.5rem', color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>#</th>
                  <th style={{ textAlign: 'left', padding: '0.35rem 0.5rem', color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '0.35rem 0.5rem', color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>{isMemoireGame ? 'Set Title' : 'Question'}</th>
                  <th style={{ textAlign: 'left', padding: '0.35rem 0.5rem', color: 'var(--text-muted)', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>{isMemoireGame ? 'Details' : 'Answer'}</th>
                </tr>
              </thead>
              <tbody>
                {previewItems.map((it, i) => {
                  const q = getPreviewDisplay(it);
                  return (
                    <tr key={it.item_id || i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '0.3rem 0.5rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{i + 1}</td>
                      <td style={{ padding: '0.3rem 0.5rem' }}>
                        <span style={{
                          display: 'inline-block', padding: '0.1rem 0.4rem', borderRadius: '0.3rem',
                          fontSize: '0.7rem', fontWeight: 700, fontFamily: 'Fredoka',
                          background: q.badgeBg, color: q.badgeColor,
                        }}>{q.type}</span>
                      </td>
                      <td style={{ padding: '0.3rem 0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>{q.question}</td>
                      <td style={{ padding: '0.3rem 0.5rem', color: 'var(--primary)', fontWeight: 600 }}>{q.answer}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function getPreviewDisplay(item) {
  const game = item.game;
  // Horloge Folle modes
  if (game === 'horloge_folle') {
    const m = item.mode;
    if (m === 'see_produce_french') return { type: 'Number', question: String(item.number ?? '?'), answer: item.french_word || '—', badgeBg: '#4C6EF512', badgeColor: '#4C6EF5' };
    if (m === 'math_in_french') return { type: 'French Math', question: item.math_display || '?', answer: item.french_word || String(item.number ?? ''), badgeBg: '#E0313112', badgeColor: '#E03131' };
    if (m === 'see_clock_produce_french' || m === 'time_reading') return { type: 'Time', question: item.time_display || '?', answer: item.time_french || item.french_word || '—', badgeBg: '#0CA67812', badgeColor: '#0CA678' };
    if (m === 'hear_type_number') return { type: 'Listen', question: item.audio_text_fr || '(audio)', answer: String(item.number ?? ''), badgeBg: '#7950F212', badgeColor: '#7950F2' };
    if (m === 'hear_type_time' || m === 'hear_produce_time') return { type: 'Listen', question: item.audio_text_fr || '(audio)', answer: item.time_display || item.time_french || '—', badgeBg: '#7950F212', badgeColor: '#7950F2' };
    if (m === 'read_set_clock') return { type: 'Read', question: item.french_word || '?', answer: item.time_display || '—', badgeBg: '#0CA67812', badgeColor: '#0CA678' };
    if (m === 'time_with_future') return { type: 'Future', question: item.sentence_fr || '?', answer: item.time_french || '—', badgeBg: '#0CA67812', badgeColor: '#0CA678' };
  }
  // Conjugation drill (Le Duel)
  if (item.exercise_type === 'conjugation_drill') {
    const q = item.prompt_text_fr || `${item.verb} — ${item.subject} → ?`;
    const a = item.correct_form || (item.options || []).find(o => o.correct)?.text || '—';
    return { type: 'Verb', question: q.length > 50 ? q.slice(0, 47) + '...' : q, answer: a, badgeBg: '#E0313112', badgeColor: '#E03131' };
  }
  // La Tour items — show French prompt and correct option
  if (game === 'la_tour') {
    const et = item.exercise_type || '';
    const labelMap = { article_selection: 'Article', preposition_selection: 'Prep', conjugation_drill: 'Conj', verb_selection: 'Verb', auxiliary_selection: 'Aux', agreement_selection: 'Agree', tense_sorting: 'Tense', sentence_transformation: 'Transform', word_image_speed_match: 'Vocab', comparison_selection: 'Compare' };
    const colorMap = { article_selection: '#4C6EF5', preposition_selection: '#4C6EF5', conjugation_drill: '#E03131', verb_selection: '#E03131', auxiliary_selection: '#E03131', agreement_selection: '#E03131', tense_sorting: '#0CA678', sentence_transformation: '#0CA678', word_image_speed_match: '#7950F2', comparison_selection: '#7950F2' };
    const type = labelMap[et] || 'Q';
    const color = colorMap[et] || '#4C6EF5';
    const q = item.prompt_text_fr || item.prompt_text_en || '—';
    const a = (item.options || []).find(o => o.correct)?.text || '—';
    return { type, question: q.length > 50 ? q.slice(0, 47) + '...' : q, answer: a, badgeBg: `${color}12`, badgeColor: color };
  }
  // Memoire items — show set title, match type, pair count
  if (game === 'memoire') {
    const title = item.title || 'Memory Set';
    const mt = item.match_type || 'french_english';
    const mtLabels = {
      french_english: 'FR↔EN', infinitive_conjugated: 'Verb', infinitive_past_participle: 'P.P.',
      french_french: 'FR↔FR', masculine_feminine: 'M↔F', question_answer: 'Q↔A',
      antonym_pairs: 'Opp', antonym: 'Opp', verb_conjugation: 'Verb', subject_agreement: 'Agree',
      sentence_halves: 'Phrase', pronoun_verb_form: 'Pro↔V', pronoun_avoir: 'Avoir',
      profession_workplace: 'Prof', present_future: 'Tense', auxiliary_verb: 'Aux', affirmative_negative: '+/–',
    };
    const mtColors = {
      french_english: '#4C6EF5', infinitive_conjugated: '#E03131', infinitive_past_participle: '#7950F2',
      french_french: '#0CA678', masculine_feminine: '#F06595', question_answer: '#F59F00',
      antonym_pairs: '#E03131', antonym: '#E03131', verb_conjugation: '#E03131',
    };
    const typeLabel = mtLabels[mt] || mt;
    const color = mtColors[mt] || '#4C6EF5';
    const pairCount = (item.pairs || []).length;
    const gs = item.grid_size || '4x4';
    const sample = (item.pairs || []).slice(0, 2).map(p => `${p.card_a}↔${p.card_b}`).join(', ');
    return {
      type: typeLabel,
      question: title.length > 45 ? title.slice(0, 42) + '...' : title,
      answer: `${pairCount} pairs (${gs})` + (sample ? ` \u2014 ${sample}` : ''),
      badgeBg: `${color}12`, badgeColor: color,
    };
  }
  // Tri Express items — show sort type, title, categories + word count
  if (game === 'tri_express') {
    const st = item.sort_type || 'unknown';
    const stLabels = { vocabulary_category: 'Vocab', gender: 'Gender', tense: 'Tense', tense_sorting: 'Tense', comparison_type: 'Compare', conjunction_type: 'Conj', verb_type: 'Verb', auxiliary_selection: 'Aux', grammar_category: 'Grammar', opinion_vs_fact: 'Opinion', grammar: 'Grammar', agreement_form: 'Agree', agreement_number: 'Number', auxiliary_in_context: 'Aux' };
    const stColors = { vocabulary_category: '#4C6EF5', gender: '#F06595', tense: '#0CA678', tense_sorting: '#0CA678' };
    const typeLabel = stLabels[st] || st;
    const color = stColors[st] || '#7950F2';
    const title = item.title || item.title_en || 'Sort';
    const catLabels = (item.categories || []).map(c => c.label).join(' / ');
    const wordCount = (item.items || []).length;
    return {
      type: typeLabel,
      question: title.length > 45 ? title.slice(0, 42) + '...' : title,
      answer: `${wordCount} words (${catLabels})`,
      badgeBg: `${color}12`, badgeColor: color,
    };
  }
  // Batisseur items — show English meaning and completed sentence
  if (game === 'batisseur') {
    const english = item.english_meaning || 'Build a sentence';
    const sentence = item.completed_sentence || (item.correct_order || []).join(' ');
    const wordCount = (item.correct_order || []).length;
    const diffColor = { introductory: '#4C6EF5', practice: '#F59F00', mastery: '#E03131' };
    const diffLabel = { introductory: 'Intro', practice: 'Practice', mastery: 'Mastery' };
    const d = item.difficulty || 'practice';
    return {
      type: `${wordCount}w`,
      question: english.length > 50 ? english.slice(0, 47) + '...' : english,
      answer: sentence.length > 45 ? sentence.slice(0, 42) + '...' : sentence,
      badgeBg: `${diffColor[d] || '#4C6EF5'}12`, badgeColor: diffColor[d] || '#4C6EF5',
    };
  }
  // Robot Balayeur items — show title, content type, word count
  if (game === 'robot_balayeur') {
    const ctLabels = { vocabulary_theme: 'Vocab', sentence_reading: 'Sentence', phonics_pattern: 'Phonics', minimal_pairs: 'Pairs', fluency_passage: 'Fluency', tongue_twister: 'Twister', number_sequence: 'Numbers', verb_conjugation: 'Conj.', verb_conjugation_chain: 'V.Chain', conjugation_chain: 'C.Chain' };
    const ctColors = { vocabulary_theme: '#4C6EF5', sentence_reading: '#0CA678', phonics_pattern: '#7950F2', minimal_pairs: '#F06595', fluency_passage: '#E03131', tongue_twister: '#F59F00', number_sequence: '#1971C2', verb_conjugation: '#E03131', verb_conjugation_chain: '#E03131', conjugation_chain: '#E03131' };
    const ct = item.content_type || 'vocabulary_theme';
    const typeLabel = ctLabels[ct] || ct;
    const color = ctColors[ct] || '#4C6EF5';
    const title = item.title || item.title_en || 'Reading';
    const wordCount = (item.words || []).length;
    const sampleWords = (item.words || []).slice(0, 4).join(', ');
    return {
      type: typeLabel,
      question: title.length > 45 ? title.slice(0, 42) + '...' : title,
      answer: `${wordCount} words` + (sampleWords ? ` (${sampleWords}...)` : ''),
      badgeBg: `${color}12`, badgeColor: color,
    };
  }
  // Avalanche items — show category and word counts
  if (game === 'avalanche') {
    const cat = item.target_category_fr || item.target_instruction || '—';
    const correctCount = (item.correct_words || []).length;
    const distractorCount = (item.distractor_words || []).length;
    const sampleWords = (item.correct_words || []).slice(0, 3).join(', ');
    return {
      type: 'Category',
      question: cat.length > 50 ? cat.slice(0, 47) + '...' : cat,
      answer: `${correctCount} correct, ${distractorCount} distract.` + (sampleWords ? ` (${sampleWords}...)` : ''),
      badgeBg: '#1971C212', badgeColor: '#1971C2',
    };
  }
  // Detective items — show audio text and correct answer
  if (game === 'detective') {
    const et = item.exercise_type || 'listening_identification';
    const isDialogue = et === 'dialogue_completion';
    const typeLabel = isDialogue ? 'Dialogue' : 'Listen';
    const color = isDialogue ? '#0CA678' : '#7950F2';
    const q = item.audio_text_fr || '—';
    const correctOpt = (item.options || []).find(o => o.correct);
    const a = correctOpt?.label || correctOpt?.text || '—';
    return {
      type: typeLabel,
      question: q.length > 55 ? q.slice(0, 52) + '...' : q,
      answer: a.length > 40 ? a.slice(0, 37) + '...' : a,
      badgeBg: `${color}12`, badgeColor: color,
    };
  }
  // Carte du Canada items — show question and correct answer with mode badge
  if (game === 'carte_du_canada') {
    const modeLabels = { trivia: 'Trivia', cultural_trivia: 'Culture', navigation: 'Nav', direction_navigation: 'Nav', direction: 'Dir', audio_locate: 'Listen' };
    const modeColors = { trivia: '#4C6EF5', cultural_trivia: '#7950F2', navigation: '#0CA678', direction_navigation: '#0CA678', direction: '#0CA678', audio_locate: '#E03131' };
    const m = item.mode || 'trivia';
    const typeLabel = modeLabels[m] || 'Q';
    const color = modeColors[m] || '#4C6EF5';
    const q = item.prompt_text_en || item.prompt_text_fr || '—';
    const correctOpt = (item.options || []).find(o => o.correct);
    const a = correctOpt?.text || '—';
    return {
      type: typeLabel,
      question: q.length > 55 ? q.slice(0, 52) + '...' : q,
      answer: a.length > 40 ? a.slice(0, 37) + '...' : a,
      badgeBg: `${color}12`, badgeColor: color,
    };
  }
  // Generic: MC games, other games
  const q = item.prompt_text_en || item.prompt_text_fr || item.english || item.french_word || item.sentence_fr || item.question || '—';
  const a = item.correct_answer || item.correct_form || item.french || item.french_word || item.english_word || (item.options || []).find(o => o.correct)?.text || '—';
  // Determine type from prompt_type or exercise_type
  const ptype = item.prompt_type || item.exercise_type || '';
  const typeLabel = ptype === 'audio' ? 'Listen' : ptype === 'image' ? 'Image' : ptype === 'text' ? 'Text' : 'Q';
  return { type: typeLabel, question: q.length > 50 ? q.slice(0, 47) + '...' : q, answer: a.length > 40 ? a.slice(0, 37) + '...' : a, badgeBg: '#4C6EF512', badgeColor: '#4C6EF5' };
}

export function UnifiedGameSetup() {
  const { game } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { playSound } = useSound();
  const [contentCounts, setContentCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const mode = searchParams.get('mode') || 'school';
  const grade = searchParams.get('grade');
  const month = searchParams.get('month');
  const level = searchParams.get('level');
  const topic = searchParams.get('topic');
  const filterDifficulty = searchParams.get('difficulty');
  const itemId = searchParams.get('item_id');
  // True only for game launches from a Program LESSON (which carry a dedicated lesson bank).
  // Used to lock difficulty (don't expand the pool). Program game-only blocks set returnTo for
  // navigation but should keep the normal auto-merge behavior, so we key off the lesson params.
  const isLessonContext = searchParams.get('lessonBank') === '1' || !!searchParams.get('lessonId');

  const config = GAME_CONFIG[game] || { name: game, itemLabel: 'items', participation: ['solo'], showGameTimer: false, showTarget: false, showPerQuestionTimer: false, showRounds: true, showGrid: false, defaultParticipation: 'solo' };

  const [settings, setSettings] = useState({
    difficulty: filterDifficulty || '',
    participation: searchParams.get('participation') || config.defaultParticipation || 'solo',
    target: searchParams.has('target') ? parseInt(searchParams.get('target')) : 999,
    rounds: searchParams.has('rounds') ? parseInt(searchParams.get('rounds')) : 999,
    gameTimer: searchParams.has('gameTimer') ? parseInt(searchParams.get('gameTimer')) : 0,
    perQuestionTimer: searchParams.has('timer') ? parseInt(searchParams.get('timer')) : 20,
    team1Name: searchParams.get('team1Name') || 'Team 1',
    team2Name: searchParams.get('team2Name') || 'Team 2',
    gridSize: searchParams.get('gridSize') || '4x4',
    wordSpeed: searchParams.has('wordSpeed') ? parseFloat(searchParams.get('wordSpeed')) : 7,
    passageRepeat: searchParams.has('passageRepeat') ? parseInt(searchParams.get('passageRepeat')) : 2,
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Add to Playlist
  const { user } = useAuth();
  const [playlistPanel, setPlaylistPanel] = useState('closed'); // closed | picking | naming | adding | done
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(null);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [addedLabel, setAddedLabel] = useState('');
  // Horloge Folle: question type filter
  const [questionType, setQuestionType] = useState('all'); // all | numbers | time | listening | french_math
  const [typeCounts, setTypeCounts] = useState(null); // { numbers: N, time: N, ... }
  // Robot Balayeur: passage picker
  const [passages, setPassages] = useState([]); // fetched passage items
  const [selectedPassages, setSelectedPassages] = useState(new Set()); // indices of selected passages (empty = all)
  const [expandedPassage, setExpandedPassage] = useState(null); // index of expanded passage (null = none)

  const baseParams = { mode };
  if (mode === 'school') { baseParams.grade = grade; baseParams.month = month; }
  if (mode === 'level') { baseParams.level = level; if (topic) baseParams.topic = topic; }
  if (mode === 'topic') { baseParams.topic = topic; }
  if (itemId) baseParams.item_id = itemId;

  useEffect(() => {
    setLoading(true);
    // Always fetch all difficulty counts so auto-merge can work in level/topic modes
    Promise.all(
      ['introductory', 'practice', 'mastery'].map(d =>
        axios.get(`${API}/content/browse`, { params: { ...baseParams, game, difficulty: d, limit: 1 } })
          .then(r => ({ difficulty: d, count: r.data.count }))
      )
    ).then(results => {
      const counts = {};
      results.forEach(r => { counts[r.difficulty] = r.count; });
      setContentCounts(counts);
      if (filterDifficulty) {
        setSettings(s => ({ ...s, difficulty: filterDifficulty }));
      } else {
        const firstWithContent = ['introductory', 'practice', 'mastery'].find(d => counts[d] > 0);
        if (firstWithContent) setSettings(s => ({ ...s, difficulty: firstWithContent }));
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [mode, grade, month, level, topic, game, filterDifficulty]);

  const availableDiffs = filterDifficulty
    ? (contentCounts[filterDifficulty] > 0 ? [filterDifficulty] : [])
    : ['introductory', 'practice', 'mastery'].filter(d => (contentCounts[d] || 0) > 0);
  const isTeamMode = ['teams', 'head_to_head'].includes(settings.participation);
  const isFilteredMode = mode === 'level' || mode === 'topic';

  // Auto-merge: in level/topic mode, if selected difficulty has too few items, merge with adjacent
  // Team games ALWAYS get auto-merge regardless of browse mode (they split items between 2 teams)
  // EXCEPTION: When launched from a lesson (returnTo param), lock difficulty — don't expand the pool
  const isTeamGame = ['la_course', 'duel_conjugaison'].includes(game);
  const mergeInfo = useMemo(() => {
    if (isLessonContext || (!settings.difficulty || (!isFilteredMode && !isTeamGame))) {
      const count = contentCounts[settings.difficulty] || 0;
      return { difficulties: settings.difficulty ? [settings.difficulty] : [], totalCount: count, merged: false, addedDiffs: [] };
    }
    const threshold = MIN_ITEMS_THRESHOLD[game] || 5;
    const stopAt = isTeamGame ? TEAM_STOP_THRESHOLD : undefined;
    return computeMergedDifficulties(settings.difficulty, contentCounts, threshold, stopAt);
  }, [settings.difficulty, contentCounts, game, isFilteredMode, isTeamGame, isLessonContext]);

  // The effective count of items that will be used (merged if applicable)
  const selectedCount = mergeInfo.totalCount;

  // Pre-compute effective (post-merge) counts for each difficulty button label
  const effectiveCountPerDiff = useMemo(() => {
    const result = {};
    const threshold = MIN_ITEMS_THRESHOLD[game] || 5;
    const stopAt = isTeamGame ? TEAM_STOP_THRESHOLD : undefined;
    DIFFICULTY_ORDER.forEach(d => {
      const raw = contentCounts[d] || 0;
      if (raw === 0) { result[d] = 0; return; }
      if (isFilteredMode || isTeamGame) {
        const merge = computeMergedDifficulties(d, contentCounts, threshold, stopAt);
        result[d] = merge.totalCount;
      } else {
        result[d] = raw;
      }
    });
    return result;
  }, [contentCounts, game, isFilteredMode, isTeamGame]);

  // Effective difficulty param: comma-separated if merged, single otherwise
  const effectiveDifficulty = mergeInfo.merged ? mergeInfo.difficulties.join(',') : (settings.difficulty || '');

  // Fetch preview items when toggled
  useEffect(() => {
    if (!previewOpen || !settings.difficulty) { setPreviewItems([]); return; }
    setPreviewLoading(true);
    axios.get(`${API}/content/browse`, { params: { ...baseParams, game, difficulty: effectiveDifficulty, limit: 999 } })
      .then(r => {
        let items = r.data.items || [];
        // Filter by questionType for horloge_folle
        if (game === 'horloge_folle' && questionType !== 'all') {
          items = items.filter(it => getHorlogeTypeKey(it.mode) === questionType);
        }
        // Filter by questionType for la_tour
        if (game === 'la_tour' && questionType !== 'all') {
          items = items.filter(it => getLatourTypeKey(it.exercise_type) === questionType);
        }
        // Filter by questionType for tri_express
        if (game === 'tri_express' && questionType !== 'all') {
          items = items.filter(it => getTriTypeKey(it.sort_type) === questionType);
        }
        setPreviewItems(items);
        setPreviewLoading(false);
      })
      .catch(() => setPreviewLoading(false));
  }, [previewOpen, settings.difficulty, questionType, effectiveDifficulty]);

  // Horloge Folle, La Tour, Tri Express: fetch type counts when difficulty changes
  useEffect(() => {
    if (!['horloge_folle', 'la_tour', 'tri_express'].includes(game) || !settings.difficulty) { setTypeCounts(null); return; }
    axios.get(`${API}/content/browse`, { params: { ...baseParams, game, difficulty: effectiveDifficulty, limit: 999 } })
      .then(r => {
        const items = r.data.items || [];
        if (game === 'horloge_folle') {
          const counts = { numbers: 0, time: 0, listening: 0, french_math: 0 };
          items.forEach(it => {
            const key = getHorlogeTypeKey(it.mode);
            if (counts[key] !== undefined) counts[key]++;
          });
          setTypeCounts(counts);
        } else if (game === 'la_tour') {
          const counts = { articles: 0, conjugation: 0, tenses: 0, vocabulary: 0 };
          items.forEach(it => {
            const key = getLatourTypeKey(it.exercise_type);
            if (counts[key] !== undefined) counts[key]++;
          });
          setTypeCounts(counts);
        } else if (game === 'tri_express') {
          const counts = { vocabulary: 0, gender: 0, tenses: 0, grammar: 0 };
          items.forEach(it => {
            const key = getTriTypeKey(it.sort_type);
            if (counts[key] !== undefined) counts[key]++;
          });
          setTypeCounts(counts);
        }
      })
      .catch(() => setTypeCounts(null));
  }, [game, settings.difficulty, effectiveDifficulty]);

  // Robot Balayeur: fetch passages for picker when difficulty changes
  useEffect(() => {
    if (game !== 'robot_balayeur' || !settings.difficulty) { setPassages([]); return; }
    axios.get(`${API}/content/browse`, { params: { ...baseParams, game, difficulty: effectiveDifficulty, limit: 999 } })
      .then(r => {
        const items = (r.data.items || []).filter(i => i.words && i.words.length > 0);
        setPassages(items);
        setSelectedPassages(new Set()); // reset selection = all
      })
      .catch(() => setPassages([]));
  }, [game, settings.difficulty, effectiveDifficulty]);

  const buildConfigObj = () => ({
    mode, game,
    ...(grade && { grade }), ...(month && { month }),
    ...(level && { level }), ...(topic && { topic }),
    difficulty: effectiveDifficulty,
    participation: settings.participation,
    rounds: String(settings.rounds),
    target: String(settings.target),
    gameTimer: String(settings.gameTimer),
    timer: String(settings.perQuestionTimer),
    team1Name: settings.team1Name,
    team2Name: settings.team2Name,
    gridSize: settings.gridSize,
  });

  const openPlaylistPicker = async () => {
    try {
      const res = await axios.get(`${API}/teacher/playlists`);
      setPlaylists(res.data);
    } catch { setPlaylists([]); }
    setPlaylistPanel('picking');
  };

  const handleAddToPlaylist = async (playlistId) => {
    setSelectedPlaylistId(playlistId);
    setPlaylistPanel('adding');
    try {
      const configObj = buildConfigObj();
      const itemName = `${GAME_CONFIG[game]?.name || game} – ${topic || level || month || 'Setup'}`;
      // Add directly to playlist — no preset needed
      await axios.post(`${API}/teacher/playlists/${playlistId}/items`, {
        game, config: configObj, name: itemName,
      });
      const pl = playlists.find(p => p.id === playlistId);
      setAddedLabel(pl?.name || 'playlist');
      setPlaylistPanel('done');
      setTimeout(() => { setPlaylistPanel('closed'); setAddedLabel(''); }, 1800);
    } catch {
      setPlaylistPanel('picking');
    }
  };

  const handleCreateAndAdd = async () => {
    if (!newPlaylistName.trim()) return;
    setPlaylistPanel('adding');
    try {
      const createRes = await axios.post(`${API}/teacher/playlists`, { name: newPlaylistName.trim() });
      const newId = createRes.data.id;
      const configObj = buildConfigObj();
      const itemName = `${GAME_CONFIG[game]?.name || game} – ${topic || level || month || 'Setup'}`;
      await axios.post(`${API}/teacher/playlists/${newId}/items`, {
        game, config: configObj, name: itemName,
      });
      setAddedLabel(newPlaylistName.trim());
      setPlaylistPanel('done');
      setNewPlaylistName('');
      setTimeout(() => { setPlaylistPanel('closed'); setAddedLabel(''); }, 1800);
    } catch {
      setPlaylistPanel('naming');
    }
  };

  const handleStart = (classroom = false) => {
    if (!settings.difficulty) return;
    playSound('click');
    const params = new URLSearchParams(searchParams);
    // If merged, pass comma-separated difficulties so backend returns items from all included levels
    params.set('difficulty', effectiveDifficulty);
    params.set('participation', settings.participation);
    params.set('rounds', String(settings.rounds));
    params.set('target', String(settings.target));
    params.set('gameTimer', String(settings.gameTimer));
    params.set('timer', String(settings.perQuestionTimer));
    params.set('team1Name', settings.team1Name);
    params.set('team2Name', settings.team2Name);
    params.set('gridSize', settings.gridSize);
    if (game === 'avalanche') params.set('wordSpeed', String(settings.wordSpeed));
    if (game === 'robot_balayeur') {
      params.set('passageRepeat', String(settings.passageRepeat));
      // Pass selected passage IDs if not all are selected
      if (selectedPassages.size > 0 && selectedPassages.size < passages.length) {
        const ids = passages.filter((_, i) => selectedPassages.has(i)).map(p => p.item_id).join(',');
        params.set('selectedPassages', ids);
      }
    }
    if (game === 'horloge_folle' && questionType !== 'all') params.set('questionType', questionType);
    if (game === 'la_tour' && questionType !== 'all') params.set('questionType', questionType);
    if (game === 'tri_express' && questionType !== 'all') params.set('questionType', questionType);
    if (game === 'horloge_folle') params.set('rounds', '999'); // play all questions of selected type
    if (classroom) params.set('classroom', 'true');
    // When this setup screen was opened from a lesson or program (returnTo present), replace it in
    // history so a back gesture out of the running game returns to that origin, not this setup window.
    const hasReturnTo = !!searchParams.get('returnTo');
    navigate(`/play/${game}?${params}`, hasReturnTo ? { replace: true } : undefined);
  };

  // Teach First: same settings/params as handleStart, but route to the teach
  // walkthrough first. From there the teacher can hit Start Game any time to
  // jump into the live round.
  const handleTeachFirst = () => {
    if (!settings.difficulty) return;
    playSound('click');
    const params = new URLSearchParams(searchParams);
    params.set('difficulty', effectiveDifficulty);
    params.set('participation', settings.participation);
    params.set('rounds', String(settings.rounds));
    params.set('target', String(settings.target));
    params.set('gameTimer', String(settings.gameTimer));
    params.set('timer', String(settings.perQuestionTimer));
    params.set('team1Name', settings.team1Name);
    params.set('team2Name', settings.team2Name);
    params.set('gridSize', settings.gridSize);
    if (game === 'avalanche') params.set('wordSpeed', String(settings.wordSpeed));
    if (game === 'robot_balayeur') {
      params.set('passageRepeat', String(settings.passageRepeat));
      if (selectedPassages.size > 0 && selectedPassages.size < passages.length) {
        const ids = passages.filter((_, i) => selectedPassages.has(i)).map(p => p.item_id).join(',');
        params.set('selectedPassages', ids);
      }
    }
    if (game === 'horloge_folle' && questionType !== 'all') params.set('questionType', questionType);
    if (game === 'la_tour' && questionType !== 'all') params.set('questionType', questionType);
    if (game === 'tri_express' && questionType !== 'all') params.set('questionType', questionType);
    navigate(`/play/${game}/teach?${params}`);
  };

  // Auto-adjust target for team games when content count changes
  useEffect(() => {
    if (!config.showTarget || !isTeamMode || selectedCount === 0) return;
    const perTeamMax = Math.floor(selectedCount / 2);
    if (perTeamMax > 0 && settings.target > perTeamMax) {
      setSettings(s => ({ ...s, target: perTeamMax }));
    }
  }, [selectedCount, isTeamMode, config.showTarget]);

  // Breadcrumb
  const breadcrumbs = [];
  if (mode === 'school') { breadcrumbs.push(`Grade ${grade}`, MONTH_FR[month] || month); }
  if (mode === 'level') { breadcrumbs.push(LEVEL_LABELS[level] || level); if (topic) breadcrumbs.push(TOPIC_LABELS[topic] || topic); }
  if (mode === 'topic') { breadcrumbs.push(TOPIC_LABELS[topic] || topic); if (filterDifficulty) breadcrumbs.push(filterDifficulty.charAt(0).toUpperCase() + filterDifficulty.slice(1)); }
  breadcrumbs.push(config.name);

  const handleBack = () => {
    // Launched from a lesson or program: return to that origin instead of the games browser.
    const returnTo = searchParams.get('returnTo');
    if (returnTo) {
      const rs = parseInt(searchParams.get('returnSlide') || '', 10);
      const backSlide = !isNaN(rs) && rs > 0 ? rs - 1 : null; // the game-launch slide
      navigate(returnTo + (backSlide != null ? `?slide=${backSlide}` : ''));
      return;
    }
    const params = new URLSearchParams(searchParams);
    ['participation', 'rounds', 'target', 'gameTimer', 'timer', 'team1Name', 'team2Name', 'gridSize'].forEach(k => params.delete(k));
    if (!filterDifficulty) params.delete('difficulty');
    navigate(`/games?${params}`);
  };

  if (loading) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.5rem', color: 'var(--text-secondary)' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="game-setup-screen" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <NavBar items={breadcrumbs} onBack={handleBack} />
      <div style={{ flex: 1, padding: '1.5rem', overflow: 'auto', position: 'relative' }}>
        {/* Teach First top-right pill — hidden for slide-taught lesson/program flows (which already teach via slides) */}
        {!searchParams.get('returnTo') && (
          <TeachFirstTopRight
            onClick={handleTeachFirst}
            disabled={!settings.difficulty || selectedCount === 0}
          />
        )}
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Fredoka', fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)', textAlign: 'center', marginBottom: '0.25rem' }}>
            {config.name}
          </h2>

          {/* Game description */}
          <p data-testid="game-description" style={{
            textAlign: 'center', color: 'var(--text-secondary)', fontSize: '1rem',
            marginBottom: '1rem', lineHeight: 1.4, maxWidth: 500, margin: '0 auto 1rem',
          }}>
            {config.desc}
          </p>

          {/* Curriculum Map button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <CurriculumMapButton
              queryParams={
                isLessonContext && searchParams.get('lessonId')
                  ? { ...baseParams, lessonId: searchParams.get('lessonId'), game }
                  : { ...baseParams, game }
              }
              title={`${config.name} — Curriculum Alignment`}
            />
          </div>

          {/* Difficulty */}
          {!filterDifficulty && (
            <SettingGroup label="DIFFICULTY">
              {availableDiffs.length > 0 ? (
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                  {availableDiffs.map(d => {
                    const count = contentCounts[d] || 0;
                    const label = count === 1 ? config.itemLabel.replace(/s$/, '') : config.itemLabel;
                    return (
                      <SettingButton key={d} active={settings.difficulty === d}
                        onClick={() => { playSound('click'); setSettings(s => ({ ...s, difficulty: d })); }}>
                        {d.charAt(0).toUpperCase() + d.slice(1)}
                        <span style={{ fontSize: '0.85rem', opacity: 0.7, marginLeft: '0.5rem' }}>
                          ({count} {label})
                        </span>
                      </SettingButton>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--error)', fontSize: '1.1rem' }}>
                  <AlertTriangle size={20} /> No content available for this game
                </div>
              )}
            </SettingGroup>
          )}

          {filterDifficulty && (
            <SettingGroup label="DIFFICULTY">
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <SettingButton active={true} onClick={() => {}}>
                  {filterDifficulty.charAt(0).toUpperCase() + filterDifficulty.slice(1)}
                  <span style={{ fontSize: '0.85rem', opacity: 0.7, marginLeft: '0.5rem' }}>
                    ({selectedCount} {selectedCount === 1 ? config.itemLabel.replace(/s$/, '') : config.itemLabel})
                  </span>
                </SettingButton>
              </div>
            </SettingGroup>
          )}

          {/* Auto-merge info banner — shown when difficulty was too thin and we merged */}
          {mergeInfo.merged && settings.difficulty && (
            <div data-testid="merge-info-banner" style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
              padding: '0.75rem 1rem', marginBottom: '1.25rem',
              background: 'rgba(76, 110, 245, 0.08)',
              border: '2px solid rgba(76, 110, 245, 0.2)',
              borderRadius: '0.75rem',
            }}>
              <Info size={20} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontFamily: 'Nunito', fontSize: '0.95rem', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                {(() => {
                  const rawCount = contentCounts[settings.difficulty] || 0;
                  const rawLabel = rawCount === 1 ? config.itemLabel.replace(/s$/, '') : config.itemLabel;
                  const addedLabel = mergeInfo.addedDiffs.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(' & ');
                  const totalLabel = selectedCount === 1 ? config.itemLabel.replace(/s$/, '') : config.itemLabel;
                  return (
                    <>
                      <strong style={{ color: 'var(--text-primary)' }}>
                        {settings.difficulty.charAt(0).toUpperCase() + settings.difficulty.slice(1)}
                      </strong>
                      {' '}has only{' '}
                      <strong>{rawCount} {rawLabel}</strong>
                      {' '}&mdash;{' '}
                      <strong style={{ color: 'var(--primary)' }}>{addedLabel}</strong>
                      {' '}{config.itemLabel} included for a better game experience
                      {' '}({selectedCount} {totalLabel} total)
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Preview Questions — for horloge_folle, la_tour, and tri_express, shown after type selector; for others, shown after difficulty */}
          {game !== 'horloge_folle' && game !== 'la_tour' && game !== 'tri_express' && settings.difficulty && selectedCount > 0 && (
            <PreviewSection previewOpen={previewOpen} setPreviewOpen={setPreviewOpen} previewLoading={previewLoading} previewItems={previewItems} gameName={game} />
          )}

          {/* Horloge Folle: Question Type Selector */}
          {game === 'horloge_folle' && typeCounts && settings.difficulty && (
            <SettingGroup label="QUESTION TYPE">
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {[
                  { key: 'all', label: 'All Types', count: Object.values(typeCounts).reduce((a, b) => a + b, 0), color: '#4C6EF5' },
                  { key: 'numbers', label: 'Numbers', count: typeCounts.numbers, color: '#4C6EF5' },
                  { key: 'time', label: 'Time', count: typeCounts.time, color: '#0CA678' },
                  { key: 'listening', label: 'Listening', count: typeCounts.listening, color: '#7950F2' },
                  { key: 'french_math', label: 'French Math', count: typeCounts.french_math, color: '#E03131' },
                ].filter(t => t.key === 'all' || t.count > 0).map(t => (
                  <SettingButton key={t.key} active={questionType === t.key}
                    onClick={() => { playSound('click'); setQuestionType(t.key); setPreviewOpen(false); }}>
                    <span style={{ color: questionType === t.key ? 'white' : t.color, fontWeight: 700 }}>{t.label}</span>
                    <span style={{ fontSize: '0.85rem', opacity: 0.7, marginLeft: '0.4rem' }}>({t.count})</span>
                  </SettingButton>
                ))}
              </div>
            </SettingGroup>
          )}

          {/* Preview Questions — for horloge_folle, after type selector */}
          {game === 'horloge_folle' && settings.difficulty && selectedCount > 0 && (
            <PreviewSection previewOpen={previewOpen} setPreviewOpen={setPreviewOpen} previewLoading={previewLoading} previewItems={previewItems} gameName={game} />
          )}

          {/* La Tour: Question Type Selector */}
          {game === 'la_tour' && typeCounts && settings.difficulty && (
            <SettingGroup label="QUESTION TYPE">
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {[
                  { key: 'all', label: 'All Types', count: Object.values(typeCounts).reduce((a, b) => a + b, 0), color: '#4C6EF5' },
                  { key: 'articles', label: 'Articles & Prep', count: typeCounts.articles, color: '#4C6EF5' },
                  { key: 'conjugation', label: 'Conjugation', count: typeCounts.conjugation, color: '#E03131' },
                  { key: 'tenses', label: 'Tenses', count: typeCounts.tenses, color: '#0CA678' },
                  { key: 'vocabulary', label: 'Vocabulary', count: typeCounts.vocabulary, color: '#7950F2' },
                ].filter(t => t.key === 'all' || t.count > 0).map(t => (
                  <SettingButton key={t.key} active={questionType === t.key}
                    onClick={() => { playSound('click'); setQuestionType(t.key); setPreviewOpen(false); }}>
                    <span style={{ color: questionType === t.key ? 'white' : t.color, fontWeight: 700 }}>{t.label}</span>
                    <span style={{ fontSize: '0.85rem', opacity: 0.7, marginLeft: '0.4rem' }}>({t.count})</span>
                  </SettingButton>
                ))}
              </div>
            </SettingGroup>
          )}

          {/* Preview Questions — for la_tour, after type selector */}
          {game === 'la_tour' && settings.difficulty && selectedCount > 0 && (
            <PreviewSection previewOpen={previewOpen} setPreviewOpen={setPreviewOpen} previewLoading={previewLoading} previewItems={previewItems} gameName={game} />
          )}

          {/* Tri Express: Sort Type Selector */}
          {game === 'tri_express' && typeCounts && settings.difficulty && (
            <SettingGroup label="SORT TYPE">
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {[
                  { key: 'all', label: 'All Types', count: Object.values(typeCounts).reduce((a, b) => a + b, 0), color: '#4C6EF5' },
                  { key: 'vocabulary', label: 'Vocabulary', count: typeCounts.vocabulary, color: '#4C6EF5' },
                  { key: 'gender', label: 'Gender', count: typeCounts.gender, color: '#F06595' },
                  { key: 'tenses', label: 'Tenses', count: typeCounts.tenses, color: '#0CA678' },
                  { key: 'grammar', label: 'Grammar', count: typeCounts.grammar, color: '#7950F2' },
                ].filter(t => t.key === 'all' || t.count > 0).map(t => (
                  <SettingButton key={t.key} active={questionType === t.key}
                    onClick={() => { playSound('click'); setQuestionType(t.key); setPreviewOpen(false); }}>
                    <span style={{ color: questionType === t.key ? 'white' : t.color, fontWeight: 700 }}>{t.label}</span>
                    <span style={{ fontSize: '0.85rem', opacity: 0.7, marginLeft: '0.4rem' }}>({t.count})</span>
                  </SettingButton>
                ))}
              </div>
            </SettingGroup>
          )}

          {/* Preview Questions — for tri_express, after type selector */}
          {game === 'tri_express' && settings.difficulty && selectedCount > 0 && (
            <PreviewSection previewOpen={previewOpen} setPreviewOpen={setPreviewOpen} previewLoading={previewLoading} previewItems={previewItems} gameName={game} />
          )}

          {/* Participation — hide for horloge_folle, la_tour, and avalanche (not meaningful) */}
          {config.participation.length > 1 && game !== 'horloge_folle' && game !== 'la_tour' && game !== 'avalanche' && game !== 'la_course' && (
            <SettingGroup label="PARTICIPATION">
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {config.participation.map(p => {
                  const labels = { teams: 'Teams', head_to_head: 'Head-to-Head', solo: 'Solo', whole_class: 'Whole Class' };
                  return (
                    <SettingButton key={p} active={settings.participation === p}
                      onClick={() => { playSound('click'); setSettings(s => ({ ...s, participation: p })); }}>
                      {labels[p]}
                    </SettingButton>
                  );
                })}
              </div>
            </SettingGroup>
          )}

          {/* Team names — only for team participation */}
          {isTeamMode && (
            <SettingGroup label="TEAM NAMES">
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <input data-testid="team1-name-input" value={settings.team1Name}
                  onChange={e => setSettings(s => ({ ...s, team1Name: e.target.value }))}
                  style={{ flex: 1, minHeight: 48, padding: '0.5rem 1rem', fontSize: '1.15rem', borderRadius: '0.75rem', border: '3px solid var(--team1)', background: 'var(--surface)', color: 'var(--text-primary)', fontWeight: 600 }} />
                <input data-testid="team2-name-input" value={settings.team2Name}
                  onChange={e => setSettings(s => ({ ...s, team2Name: e.target.value }))}
                  style={{ flex: 1, minHeight: 48, padding: '0.5rem 1rem', fontSize: '1.15rem', borderRadius: '0.75rem', border: '3px solid var(--team2)', background: 'var(--surface)', color: 'var(--text-primary)', fontWeight: 600 }} />
              </div>
            </SettingGroup>
          )}

          {/* Target — "First to X" for team games */}
          {config.showTarget && (() => {
            // For team games, each team gets ~half the items, so cap the target accordingly
            const perTeamMax = isTeamMode ? Math.floor(selectedCount / 2) : selectedCount;
            const allTargets = [3, 5, 7, 10, 15, 20];
            // Filter: only show targets ≤ perTeamMax AND sensible relative to pool size
            // For team games: minimum target = half of perTeamMax (avoids absurdly short games after auto-merge)
            const minSensibleTarget = isTeamMode ? Math.max(5, Math.ceil(perTeamMax / 2)) : 3;
            const availableTargets = perTeamMax > 0
              ? allTargets.filter(t => t <= perTeamMax && t >= minSensibleTarget)
              : allTargets;
            // Ensure at least one target option exists
            if (availableTargets.length === 0 && perTeamMax > 0) availableTargets.push(perTeamMax);
            return (
              <SettingGroup label={isTeamMode ? 'FIRST TO (target score)' : 'TARGET'}>
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                  {availableTargets.map(t => (
                    <SettingButton key={t} active={settings.target === t}
                      onClick={() => { playSound('click'); setSettings(s => ({ ...s, target: t })); }}>
                      {t}
                    </SettingButton>
                  ))}
                  {/* If max items per team is not a standard option, show it as "All (N)" */}
                  {isTeamMode && perTeamMax > 0 && !allTargets.includes(perTeamMax) && (
                    <SettingButton active={settings.target === perTeamMax}
                      onClick={() => { playSound('click'); setSettings(s => ({ ...s, target: perTeamMax })); }}>
                      All ({perTeamMax})
                    </SettingButton>
                  )}
                </div>
                {isTeamMode && perTeamMax > 0 && perTeamMax < settings.target && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--accent)', fontWeight: 600 }}>
                    {selectedCount} {config.itemLabel} available ({perTeamMax} per team) — target auto-adjusted in game
                  </div>
                )}
              </SettingGroup>
            );
          })()}

          {/* Rounds — for solo games that go through sets/items (hide for horloge_folle — type filter handles it) */}
          {config.showRounds && game !== 'horloge_folle' && (
            <SettingGroup label={`HOW MANY ${config.itemLabel.toUpperCase()}`}>
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {[5, 10, 15, 20].map(r => (
                  <SettingButton key={r} active={settings.rounds === r}
                    onClick={() => { playSound('click'); setSettings(s => ({ ...s, rounds: r })); }}>
                    {r}
                  </SettingButton>
                ))}
                <SettingButton active={settings.rounds === 999}
                  onClick={() => { playSound('click'); setSettings(s => ({ ...s, rounds: 999 })); }}>
                  All
                </SettingButton>
              </div>
              {selectedCount > 0 && settings.rounds !== 999 && selectedCount < settings.rounds && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--accent)', fontWeight: 600 }}>
                  {selectedCount} {selectedCount === 1 ? config.itemLabel.replace(/s$/, '') : config.itemLabel} available — game will use {selectedCount === 1 ? 'it' : 'all of them'}
                </div>
              )}
            </SettingGroup>
          )}

          {/* Word Speed — only for Avalanche (slider) */}
          {game === 'avalanche' && (
            <SettingGroup label="WORD SPEED">
              <div style={{ padding: '0.25rem 0.5rem 0 0.5rem' }}>
                {/* Labels row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <span style={{ fontFamily: 'Fredoka', fontSize: '0.85rem', fontWeight: 600, color: '#2F9E44' }}>Slow</span>
                  <span style={{ fontFamily: 'Fredoka', fontSize: '0.85rem', fontWeight: 600, color: '#F59F00' }}>Normal</span>
                  <span style={{ fontFamily: 'Fredoka', fontSize: '0.85rem', fontWeight: 600, color: '#E03131' }}>Blitz</span>
                </div>
                {/* Slider */}
                <input
                  data-testid="word-speed-slider"
                  type="range" min="3" max="10" step="0.5"
                  value={settings.wordSpeed}
                  onChange={(e) => { setSettings(s => ({ ...s, wordSpeed: parseFloat(e.target.value) })); }}
                  onMouseUp={() => playSound('click')}
                  onTouchEnd={() => playSound('click')}
                  style={{
                    width: '100%', height: 8, appearance: 'none', WebkitAppearance: 'none',
                    background: `linear-gradient(90deg, #E03131 0%, #F59F00 50%, #2F9E44 100%)`,
                    borderRadius: 8, outline: 'none', cursor: 'pointer',
                    direction: 'rtl', // Invert so left=fast, right=slow visually matches labels
                  }}
                />
                <style>{`
                  input[data-testid="word-speed-slider"]::-webkit-slider-thumb {
                    -webkit-appearance: none; appearance: none;
                    width: 32px; height: 32px; border-radius: 50%;
                    background: white; border: 4px solid var(--primary);
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                    cursor: pointer;
                  }
                  input[data-testid="word-speed-slider"]::-moz-range-thumb {
                    width: 32px; height: 32px; border-radius: 50%;
                    background: white; border: 4px solid var(--primary);
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                    cursor: pointer;
                  }
                `}</style>
                {/* Speed description */}
                <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--text-muted)', textAlign: 'center', fontWeight: 600 }}>
                  {settings.wordSpeed >= 9 ? 'Very slow — perfect for young learners'
                    : settings.wordSpeed >= 7 ? 'Normal classroom speed'
                    : settings.wordSpeed >= 5 ? 'Fast — good for confident students'
                    : 'Blitz — expert speed!'}
                  <span style={{ marginLeft: '0.5rem', fontFamily: 'Fredoka', color: 'var(--primary)' }}>({settings.wordSpeed}s)</span>
                </div>
              </div>
            </SettingGroup>
          )}

          {/* Robot Balayeur: Passage Picker + Repeat */}
          {game === 'robot_balayeur' && passages.length > 0 && (
            <>
              <SettingGroup label="SELECT PASSAGES">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {/* Select All / Deselect toggle */}
                  <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.25rem' }}>
                    <button data-testid="select-all-passages" onClick={() => { playSound('click'); setSelectedPassages(new Set()); }}
                      style={{
                        padding: '0.3rem 0.85rem', borderRadius: '0.5rem', fontSize: '0.85rem', fontWeight: 700,
                        fontFamily: 'Fredoka', cursor: 'pointer', border: '1.5px solid var(--border)',
                        background: selectedPassages.size === 0 ? 'var(--primary)' : 'var(--surface)',
                        color: selectedPassages.size === 0 ? 'white' : 'var(--text-secondary)',
                      }}>
                      All passages
                    </button>
                  </div>
                  {/* Passage cards — expandable */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 400, overflowY: 'auto' }}>
                    {passages.map((p, i) => {
                      const isSelected = selectedPassages.size === 0 || selectedPassages.has(i);
                      const isExpanded = expandedPassage === i;
                      const title = p.title || p.title_en || `Passage ${i + 1}`;
                      const wordCount = (p.words || []).length;
                      const ctInfo = CONTENT_TYPE_INFO[p.content_type] || { label: 'Reading', color: '#4C6EF5' };
                      return (
                        <div key={p.item_id || i} data-testid={`passage-pick-${i}`} style={{
                          borderRadius: '0.75rem', border: isSelected ? '2.5px solid var(--primary)' : '2px solid var(--border)',
                          background: isSelected ? 'rgba(76,110,245,0.06)' : 'var(--surface)',
                          overflow: 'hidden',
                        }}>
                          {/* Header row */}
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                          }}
                            onClick={() => { setExpandedPassage(isExpanded ? null : i); }}
                          >
                            {/* Checkbox — separate click target for selection */}
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                playSound('click');
                                setSelectedPassages(prev => {
                                  const next = new Set(prev.size === 0 ? passages.map((_, j) => j) : prev);
                                  if (next.has(i)) { next.delete(i); if (next.size === 0) return new Set(); }
                                  else next.add(i);
                                  if (next.size === passages.length) return new Set();
                                  return next;
                                });
                              }}
                              style={{
                                width: 24, height: 24, borderRadius: '0.4rem', flexShrink: 0,
                                background: isSelected ? 'var(--primary)' : 'transparent',
                                border: isSelected ? 'none' : '2px solid var(--border)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: 'white', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
                              }}>
                              {isSelected && '✓'}
                            </div>
                            <span style={{
                              padding: '0.1rem 0.4rem', borderRadius: '0.3rem', fontSize: '0.7rem',
                              fontWeight: 700, fontFamily: 'Fredoka', background: `${ctInfo.color}12`, color: ctInfo.color,
                            }}>{ctInfo.label}</span>
                            <span style={{ flex: 1, fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {title}
                            </span>
                            <span style={{ fontFamily: 'Fredoka', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                              {wordCount} words
                            </span>
                            <ChevronDown size={16} color="var(--text-muted)" style={{
                              transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            }} />
                          </div>
                          {/* Expanded: show words */}
                          {isExpanded && (
                            <div style={{
                              padding: '0.5rem 0.75rem 0.75rem', borderTop: '1px solid var(--border)',
                              background: 'rgba(255,255,255,0.6)',
                            }}>
                              <div style={{
                                fontFamily: 'Fredoka', fontSize: 'clamp(1rem, 2vw, 1.3rem)', fontWeight: 600,
                                color: 'var(--text-primary)', lineHeight: 1.8, wordSpacing: '0.15em',
                              }}>
                                {(p.words || []).join(' ')}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: '0.25rem' }}>
                    {selectedPassages.size === 0
                      ? `All ${passages.length} passages selected`
                      : `${selectedPassages.size} of ${passages.length} passages selected`}
                  </div>
                </div>
              </SettingGroup>

              <SettingGroup label="REPEAT EACH PASSAGE">
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {[
                    { v: 1, l: 'Once' },
                    { v: 2, l: 'Twice' },
                    { v: 3, l: '3 times' },
                  ].map(r => (
                    <SettingButton key={r.v} active={settings.passageRepeat === r.v}
                      onClick={() => { playSound('click'); setSettings(s => ({ ...s, passageRepeat: r.v })); }}>
                      {r.l}
                    </SettingButton>
                  ))}
                  {/* Custom input */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <input data-testid="custom-repeat-input" type="number" min="1" max="20"
                      value={![1, 2, 3].includes(settings.passageRepeat) ? settings.passageRepeat : ''}
                      placeholder="Custom"
                      onChange={(e) => {
                        const v = parseInt(e.target.value);
                        if (v >= 1 && v <= 20) setSettings(s => ({ ...s, passageRepeat: v }));
                      }}
                      onFocus={() => {
                        if ([1, 2, 3].includes(settings.passageRepeat)) setSettings(s => ({ ...s, passageRepeat: 4 }));
                      }}
                      style={{
                        width: 80, minHeight: 48, padding: '0.5rem 0.6rem', fontSize: '1.1rem', fontWeight: 600,
                        borderRadius: '0.75rem', textAlign: 'center',
                        border: `3px solid ${![1, 2, 3].includes(settings.passageRepeat) ? 'var(--primary)' : 'var(--border)'}`,
                        background: ![1, 2, 3].includes(settings.passageRepeat) ? 'rgba(76,110,245,0.06)' : 'var(--surface)',
                        color: 'var(--text-primary)', fontFamily: 'Nunito', outline: 'none',
                      }}
                    />
                    <span style={{ fontFamily: 'Fredoka', fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 600 }}>times</span>
                  </div>
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                  {settings.passageRepeat === 1
                    ? 'Each passage plays once — quick run-through'
                    : settings.passageRepeat === 2
                    ? 'Each passage repeats twice — recommended for fluency'
                    : settings.passageRepeat <= 3
                    ? 'Each passage repeats 3 times — deep practice for younger learners'
                    : `Each passage repeats ${settings.passageRepeat} times — intensive drill`}
                </div>
              </SettingGroup>
            </>
          )}

          {/* Game timer — for team games (game-level countdown) */}
          {config.showGameTimer && (
            <SettingGroup label="GAME TIMER">
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {[
                  { v: 60, l: '1 min' }, { v: 120, l: '2 min' }, { v: 180, l: '3 min' },
                  { v: 300, l: '5 min' }, { v: 0, l: 'No limit' },
                ].map(t => (
                  <SettingButton key={t.v} active={settings.gameTimer === t.v}
                    onClick={() => { playSound('click'); setSettings(s => ({ ...s, gameTimer: t.v })); }}>
                    {t.l}
                  </SettingButton>
                ))}
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                {settings.gameTimer > 0
                  ? `Game ends when time runs out or a team reaches ${settings.target}. Whoever has more points wins.`
                  : `Game ends when a team reaches ${settings.target} or all ${config.itemLabel} are done.`}
              </div>
            </SettingGroup>
          )}

          {/* Per-question timer — only for La Tour */}
          {config.showPerQuestionTimer && (
            <SettingGroup label="TIME PER QUESTION">
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {[{ v: 8, l: '8s' }, { v: 10, l: '10s' }, { v: 15, l: '15s' }, { v: 20, l: '20s' }, { v: 0, l: 'Off' }].map(t => (
                  <SettingButton key={t.v} active={settings.perQuestionTimer === t.v}
                    onClick={() => { playSound('click'); setSettings(s => ({ ...s, perQuestionTimer: t.v })); }}>
                    {t.l}
                  </SettingButton>
                ))}
              </div>
            </SettingGroup>
          )}

          {/* Grid size — only for Memoire */}
          {config.showGrid && (
            <SettingGroup label="GRID SIZE">
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                {[{ v: '4x4', l: '4x4 (8 pairs)' }, { v: '4x6', l: '4x6 (12 pairs)' }].map(g => (
                  <SettingButton key={g.v} active={settings.gridSize === g.v}
                    onClick={() => { playSound('click'); setSettings(s => ({ ...s, gridSize: g.v })); }}>
                    {g.l}
                  </SettingButton>
                ))}
              </div>
            </SettingGroup>
          )}

          {/* Start button */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem', paddingBottom: '0.75rem', flexWrap: 'wrap' }}>
            <button data-testid="start-game-button" className="btn-arcade btn-primary"
              onClick={() => handleStart(false)}
              disabled={!settings.difficulty || selectedCount === 0}
              style={{
                fontSize: '1.75rem', padding: '0 3rem', minHeight: 72, gap: '0.75rem',
                borderBottomWidth: 8,
                opacity: (!settings.difficulty || selectedCount === 0) ? 0.5 : 1,
              }}>
              <Play size={32} /> START GAME
            </button>
            <button data-testid="launch-smartboard-button"
              onClick={() => handleStart(true)}
              disabled={!settings.difficulty || selectedCount === 0}
              style={{
                minHeight: 72, padding: '0 2rem', fontSize: '1.1rem', fontWeight: 700,
                fontFamily: 'Fredoka, sans-serif', borderRadius: '1rem',
                border: '3px solid #1E2140', borderBottom: '6px solid #1E2140',
                background: '#1E2140', color: 'white',
                cursor: (!settings.difficulty || selectedCount === 0) ? 'default' : 'pointer',
                opacity: (!settings.difficulty || selectedCount === 0) ? 0.5 : 1,
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                transition: 'all 0.12s',
              }}>
              <Monitor size={24} /> SMARTBOARD
            </button>
          </div>

          {/* Add to Playlist */}
          {user && !user.demo && settings.difficulty && selectedCount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '2rem' }}>
              {playlistPanel === 'closed' && (
                <button
                  data-testid="add-to-playlist-toggle"
                  onClick={openPlaylistPicker}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem 1.25rem', borderRadius: '0.625rem',
                    border: '2px solid var(--border)', background: 'var(--surface)',
                    fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 600,
                    color: 'var(--text-secondary)', cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <ListOrdered size={16} /> Add to Playlist
                </button>
              )}

              {playlistPanel === 'picking' && (
                <div data-testid="playlist-picker-panel" style={{
                  background: 'var(--surface)', border: '2px solid var(--primary)',
                  borderRadius: '0.75rem', padding: '0.75rem', width: 320,
                  maxHeight: 280, display: 'flex', flexDirection: 'column',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                      Add to playlist
                    </span>
                    <button onClick={() => setPlaylistPanel('closed')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px' }}>
                      <X size={16} />
                    </button>
                  </div>
                  <div style={{ overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {playlists.map(pl => (
                      <button
                        key={pl.id}
                        data-testid={`pick-playlist-${pl.id}`}
                        onClick={() => handleAddToPlaylist(pl.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.5rem 0.625rem', borderRadius: '0.5rem',
                          border: '1px solid var(--border)', background: 'transparent',
                          cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s',
                          fontFamily: 'Nunito, sans-serif',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <ListOrdered size={14} color="var(--primary)" style={{ flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {pl.name}
                          </div>
                          <div style={{ fontSize: '0.725rem', color: 'var(--text-muted)' }}>
                            {pl.item_count} item{pl.item_count !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <button
                    data-testid="new-playlist-option"
                    onClick={() => { setNewPlaylistName(''); setPlaylistPanel('naming'); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.5rem 0.625rem', borderRadius: '0.5rem', marginTop: '0.375rem',
                      border: '1px dashed var(--border)', background: 'transparent',
                      cursor: 'pointer', fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary)',
                      width: '100%', transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <Plus size={14} /> New playlist
                  </button>
                </div>
              )}

              {playlistPanel === 'naming' && (
                <div data-testid="new-playlist-panel" style={{
                  background: 'var(--surface)', border: '2px solid var(--primary)',
                  borderRadius: '0.75rem', padding: '0.75rem', width: 320,
                  display: 'flex', flexDirection: 'column', gap: '0.5rem',
                }}>
                  <span style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    New playlist
                  </span>
                  <input
                    data-testid="new-playlist-name-input"
                    type="text"
                    value={newPlaylistName}
                    onChange={e => setNewPlaylistName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreateAndAdd()}
                    placeholder="Playlist name..."
                    autoFocus
                    style={{
                      border: '1px solid var(--border)', outline: 'none', borderRadius: '0.5rem',
                      fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem',
                      padding: '0.5rem 0.625rem', background: 'var(--bg)',
                    }}
                    onFocus={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                    onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      data-testid="create-and-add-btn"
                      onClick={handleCreateAndAdd}
                      disabled={!newPlaylistName.trim()}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.375rem',
                        padding: '0.4rem 0.875rem', borderRadius: '0.5rem',
                        background: 'var(--primary)', color: 'white', border: 'none',
                        fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 600,
                        cursor: newPlaylistName.trim() ? 'pointer' : 'default',
                        opacity: newPlaylistName.trim() ? 1 : 0.5,
                      }}
                    >
                      <Plus size={14} /> Create & Add
                    </button>
                    <button
                      onClick={() => setPlaylistPanel('picking')}
                      style={{
                        padding: '0.4rem 0.75rem', borderRadius: '0.5rem',
                        background: 'transparent', color: 'var(--text-muted)',
                        border: '1px solid var(--border)',
                        fontFamily: 'Nunito, sans-serif', fontSize: '0.85rem', fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}

              {playlistPanel === 'adding' && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 1.25rem', borderRadius: '0.625rem',
                  border: '2px solid var(--border)', background: 'var(--surface)',
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', fontWeight: 600,
                  color: 'var(--text-muted)',
                }}>
                  Adding...
                </div>
              )}

              {playlistPanel === 'done' && (
                <div data-testid="playlist-added-success" style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 1.25rem', borderRadius: '0.625rem',
                  border: '2px solid #2F9E44', background: 'var(--surface)',
                  fontFamily: 'Nunito, sans-serif', fontSize: '0.875rem', fontWeight: 600,
                  color: '#2F9E44',
                }}>
                  <Check size={16} /> Added to {addedLabel}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingGroup({ label, children }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ fontFamily: 'Fredoka', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.5rem', letterSpacing: '0.03em' }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function SettingButton({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      minHeight: 48, padding: '0.5rem 1.25rem', fontSize: '1.1rem', fontWeight: 600,
      borderRadius: '0.75rem',
      border: `3px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
      borderBottom: `5px solid ${active ? '#3B5BDB' : 'var(--border)'}`,
      background: active ? 'var(--primary)' : 'var(--surface)',
      color: active ? 'white' : 'var(--text-primary)',
      cursor: 'pointer', transition: 'all 0.12s',
      fontFamily: 'Nunito', boxShadow: active ? '0 2px 8px rgba(76,110,245,0.25)' : '0 1px 4px var(--shadow)',
    }}>
      {children}
    </button>
  );
}
